import { useMemo } from 'react';
import { AnimationSettings } from '../types/design';
import { interpolateProperty, getEffectiveKeyframes } from '../utils/animationUtils';

// Basic easing functions
const easings = {
    linear: (t: number) => t,
    easeIn: (t: number) => t * t,
    easeOut: (t: number) => t * (2 - t),
    easeInOut: (t: number) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
    ease: (t: number) => t * t * (3 - 2 * t), // Simple cubic ease
    backOut: (t: number) => {
        const s = 1.70158;
        return --t * t * ((s + 1) * t + s) + 1;
    },
    elasticOut: (t: number) => {
        return Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * (2 * Math.PI) / 3) + 1;
    }
};

export const useInterpolatedAnimation = (
    currentTime: number,
    animation?: AnimationSettings,
    isPlaying?: boolean
) => {
    const result = useMemo(() => {
        if (!animation || animation.type === 'none') {
            return {};
        }

        const { delay, duration, easing = 'ease' } = animation;
        const effectiveKeyframes = getEffectiveKeyframes(animation);

        // Calculate local time within the animation
        let localTime = currentTime - delay;

        // Handle looping if enabled
        if (animation.loop && duration > 0 && localTime > 0) {
            localTime = localTime % duration;
        }

        /*
        console.log('[ANIM_DEBUG] useInterpolatedAnimation processing:', { 
            type: animation.type, 
            localTime, 
            duration, 
            hasKeyframes: animation.keyframes && animation.keyframes.length > 0,
            effectiveKeyframesCount: effectiveKeyframes.length 
        });
        */

        // If we're before the start of the animation
        if (localTime < 0) {
            // Use values from first keyframe
            if (effectiveKeyframes.length > 0) {
                return effectiveKeyframes[0].properties;
            }
            return {};
        }

        // If we're after the animation
        if (localTime >= duration) {
            if (effectiveKeyframes.length > 0) {
                return effectiveKeyframes[effectiveKeyframes.length - 1].properties;
            }
            return {};
        }

        // Apply easing to the progress
        const progress = localTime / duration;
        const easingFn = (easings as any)[easing] || easings.ease;
        const easedProgress = easingFn(progress);
        const easedLocalTime = easedProgress * duration;

        // Interpolate all properties present in keyframes
        const properties: Record<string, any> = {};
        const allPropNames = new Set<string>();
        effectiveKeyframes.forEach(kf => {
            Object.keys(kf.properties).forEach(p => allPropNames.add(p));
        });

        allPropNames.forEach(prop => {
            properties[prop] = interpolateProperty(
                easedLocalTime,
                effectiveKeyframes,
                prop,
                undefined
            );
        });

        // console.log('[ANIM_DEBUG] useInterpolatedAnimation result:', { type: animation.type, properties });

        return properties;
    }, [currentTime, animation, isPlaying]);

    return result;
};
